/**
 * useSEO Hook - Centralized SEO Management
 *
 * Manages dynamic meta tags, canonical URLs, hreflang tags,
 * Open Graph, Twitter Cards, and structured data.
 */

import { useEffect } from 'react';

export interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  path?: string; // Current path for hreflang generation
  image?: string;
  imageAlt?: string;
  type?: 'website' | 'article' | 'product';
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  noindex?: boolean;
  language?: 'pt-br' | 'en' | 'es';
  structuredData?: object | object[];
}

const BASE_URL = 'https://nexucapital.com.br';
const DEFAULT_IMAGE = `${BASE_URL}/assets/logos/logo-nexucapital-principal.png`;
const SITE_NAME = 'NexuCapital Empreendimentos';

/**
 * Hook to manage SEO meta tags dynamically
 */
export function useSEO({
  title,
  description,
  keywords,
  canonical,
  path = '/',
  image = DEFAULT_IMAGE,
  imageAlt = 'NexuCapital - Líder em intermediação de ativos minerários no Brasil',
  type = 'website',
  author = 'NexuCapital Empreendimentos LTDA',
  publishedTime,
  modifiedTime,
  noindex = false,
  language = 'pt-br',
  structuredData,
}: SEOProps): void {
  useEffect(() => {
    // Full title with brand
    const fullTitle = title.includes('NexuCapital')
      ? title
      : `${title} | NexuCapital`;

    // Set document title
    document.title = fullTitle;

    // Canonical URL
    const canonicalUrl = canonical || `${BASE_URL}${path}`;

    // Helper function to update or create meta tag
    const setMetaTag = (
      selector: string,
      attribute: string,
      value: string,
      createWith?: { name?: string; property?: string; httpEquiv?: string }
    ) => {
      let element = document.querySelector(selector) as HTMLMetaElement;
      if (!element && createWith) {
        element = document.createElement('meta');
        if (createWith.name) element.setAttribute('name', createWith.name);
        if (createWith.property) element.setAttribute('property', createWith.property);
        if (createWith.httpEquiv) element.setAttribute('http-equiv', createWith.httpEquiv);
        document.head.appendChild(element);
      }
      if (element) {
        element.setAttribute(attribute, value);
      }
    };

    // Helper to set link tag
    const setLinkTag = (rel: string, href: string, attributes?: Record<string, string>) => {
      const selector = attributes?.hreflang
        ? `link[rel="${rel}"][hreflang="${attributes.hreflang}"]`
        : `link[rel="${rel}"]`;
      let element = document.querySelector(selector) as HTMLLinkElement;
      if (!element) {
        element = document.createElement('link');
        element.rel = rel;
        document.head.appendChild(element);
      }
      element.href = href;
      if (attributes) {
        Object.entries(attributes).forEach(([key, value]) => {
          element.setAttribute(key, value);
        });
      }
    };

    // Basic Meta Tags
    setMetaTag('meta[name="description"]', 'content', description, { name: 'description' });
    if (keywords) {
      setMetaTag('meta[name="keywords"]', 'content', keywords, { name: 'keywords' });
    }
    setMetaTag('meta[name="author"]', 'content', author, { name: 'author' });
    setMetaTag(
      'meta[name="robots"]',
      'content',
      noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1',
      { name: 'robots' }
    );

    // Canonical URL
    setLinkTag('canonical', canonicalUrl);

    // Open Graph Tags
    setMetaTag('meta[property="og:title"]', 'content', fullTitle, { property: 'og:title' });
    setMetaTag('meta[property="og:description"]', 'content', description, { property: 'og:description' });
    setMetaTag('meta[property="og:url"]', 'content', canonicalUrl, { property: 'og:url' });
    setMetaTag('meta[property="og:type"]', 'content', type, { property: 'og:type' });
    setMetaTag('meta[property="og:image"]', 'content', image.startsWith('http') ? image : `${BASE_URL}${image}`, { property: 'og:image' });
    setMetaTag('meta[property="og:image:alt"]', 'content', imageAlt, { property: 'og:image:alt' });
    setMetaTag('meta[property="og:site_name"]', 'content', SITE_NAME, { property: 'og:site_name' });
    setMetaTag('meta[property="og:locale"]', 'content', language === 'pt-br' ? 'pt_BR' : language === 'en' ? 'en_US' : 'es_ES', { property: 'og:locale' });

    // Article specific
    if (type === 'article') {
      if (publishedTime) {
        setMetaTag('meta[property="article:published_time"]', 'content', publishedTime, { property: 'article:published_time' });
      }
      if (modifiedTime) {
        setMetaTag('meta[property="article:modified_time"]', 'content', modifiedTime, { property: 'article:modified_time' });
      }
    }

    // Twitter Card Tags
    setMetaTag('meta[property="twitter:card"]', 'content', 'summary_large_image', { property: 'twitter:card' });
    setMetaTag('meta[property="twitter:title"]', 'content', fullTitle, { property: 'twitter:title' });
    setMetaTag('meta[property="twitter:description"]', 'content', description, { property: 'twitter:description' });
    setMetaTag('meta[property="twitter:image"]', 'content', image.startsWith('http') ? image : `${BASE_URL}${image}`, { property: 'twitter:image' });
    setMetaTag('meta[property="twitter:image:alt"]', 'content', imageAlt, { property: 'twitter:image:alt' });

    // hreflang Tags for multi-language
    const languages = ['pt-br', 'en', 'es'] as const;
    const cleanPath = path.split('?')[0]; // Remove existing query params

    // Remove existing hreflang tags
    document.querySelectorAll('link[hreflang]').forEach((el) => el.remove());

    // Add hreflang for each language
    languages.forEach((lang) => {
      const hrefUrl = `${BASE_URL}${cleanPath}${cleanPath.includes('?') ? '&' : '?'}lang=${lang}`;
      const link = document.createElement('link');
      link.rel = 'alternate';
      link.hreflang = lang;
      link.href = hrefUrl;
      document.head.appendChild(link);
    });

    // x-default hreflang
    const xDefaultLink = document.createElement('link');
    xDefaultLink.rel = 'alternate';
    xDefaultLink.hreflang = 'x-default';
    xDefaultLink.href = `${BASE_URL}${cleanPath}`;
    document.head.appendChild(xDefaultLink);

    // Structured Data (JSON-LD)
    if (structuredData) {
      // Remove existing dynamic structured data
      document.querySelectorAll('script[data-seo-structured]').forEach((el) => el.remove());

      const dataArray = Array.isArray(structuredData) ? structuredData : [structuredData];
      dataArray.forEach((data, index) => {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.setAttribute('data-seo-structured', `dynamic-${index}`);
        script.textContent = JSON.stringify(data);
        document.head.appendChild(script);
      });
    }

    // Cleanup on unmount
    return () => {
      // Remove dynamic hreflang tags
      document.querySelectorAll('link[hreflang]').forEach((el) => el.remove());
      // Remove dynamic structured data
      document.querySelectorAll('script[data-seo-structured]').forEach((el) => el.remove());
    };
  }, [title, description, keywords, canonical, path, image, imageAlt, type, author, publishedTime, modifiedTime, noindex, language, structuredData]);
}

/**
 * Generate BreadcrumbList structured data
 */
export function generateBreadcrumbSchema(
  items: { name: string; url: string }[]
): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url.startsWith('http') ? item.url : `${BASE_URL}${item.url}`,
    })),
  };
}

/**
 * Generate WebPage structured data
 */
export function generateWebPageSchema(options: {
  name: string;
  description: string;
  url: string;
  datePublished?: string;
  dateModified?: string;
}): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: options.name,
    description: options.description,
    url: options.url.startsWith('http') ? options.url : `${BASE_URL}${options.url}`,
    isPartOf: {
      '@type': 'WebSite',
      name: SITE_NAME,
      url: BASE_URL,
    },
    ...(options.datePublished && { datePublished: options.datePublished }),
    ...(options.dateModified && { dateModified: options.dateModified }),
  };
}

/**
 * Generate Service structured data
 */
export function generateServiceSchema(options: {
  name: string;
  description: string;
  url: string;
  provider?: string;
  areaServed?: string[];
}): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: options.name,
    description: options.description,
    url: options.url.startsWith('http') ? options.url : `${BASE_URL}${options.url}`,
    provider: {
      '@type': 'Organization',
      name: options.provider || SITE_NAME,
      url: BASE_URL,
    },
    areaServed: options.areaServed?.map((area) => ({
      '@type': 'Country',
      name: area,
    })) || [{ '@type': 'Country', name: 'Brasil' }],
  };
}

export default useSEO;
